import { omit } from 'lodash';
import { useRouter } from 'next/router';
import { useUpdateUserMutation } from '@vette/data-access';
import { DashboardLayout, Form } from '@vette/ui-components';
import { SettingsForm } from './SettingsForm';
import { FormValues } from './types';
import { hooks, message, useUser } from '@vette/frontend-utils';

type Props = {
  userId: string;
};

export const SettingsPage = ({ userId }: Props) => {
  const [form] = Form.useForm<FormValues>();
  const { isVetter, isClientInterviewer } = hooks.useRole();
  const router = useRouter();

  const { user, loading } = useUser();

  const [updateUser, { loading: submitting }] = useUpdateUserMutation({
    update: cache => {
      cache.evict({ fieldName: 'user' });
      cache.evict({ fieldName: 'vetterDashboard' });
      cache.evict({ fieldName: 'clientUser' });
    },
    onCompleted: ({ updateUser }) => {
      if (updateUser?.__typename === 'User') {
        const { stripeUser, approvalStatus, photoOriginalUrl } = updateUser;

        form.setFieldsValue({
          photoOriginal: photoOriginalUrl ? { url: photoOriginalUrl } : null,
        });

        if (
          (isVetter && !stripeUser?.stripeAccountConnected) ||
          (isClientInterviewer && !user?.isProfileDataProvided)
        ) {
          return message.success(
            `Thanks for providing your details! ${
              isVetter
                ? `The next step is to go to "Payouts" and click on "Set up your Stripe account" so that you can be paid for your Vettes.`
                : `You can go online and start Vetting right away!`
            } ${
              approvalStatus !== 'ACTIVE'
                ? "Then keep an eye on your texts and emails and we'll let you know as soon as your account is activated to start taking Vettes!"
                : ''
            }`,
            isClientInterviewer ? 0 : 20
          );
        }
        message.success('Settings have been saved.');
      } else if (updateUser?.__typename === 'ValidationError') {
        updateUser?.messages?.map(validationError =>
          message.error(validationError)
        );
      }
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (!userId) {
      return;
    }

    await updateUser({
      variables: {
        userId,
        // omit because we show the button, but we can't edit it (it's a separate flow)
        input: {
          ...omit(values, 'phone', 'language'),
          photoOriginal: values.photoOriginal?.file,
          ...(values.language && {
            attributes: [
              {
                attributeId: 'language',
                values: values.language,
              },
            ],
          }),
        },
      },
    });
  };

  if (!loading && !user) {
    message.error(
      'Something went wrong. Our team was notified and is intensively working on fixing the issue.'
    );
    router.replace('/404');
  }

  return (
    <DashboardLayout loading={loading} hasStatusToggle>
      {user && (
        <SettingsForm
          userId={userId}
          form={form}
          onSubmit={handleSubmit}
          submitting={submitting}
          initialValues={user}
          stripeAccountConnected={user.stripeUser?.stripeAccountConnected}
          pushNotificationsEnabled={user.pushNotificationsEnabled}
        />
      )}
    </DashboardLayout>
  );
};
